import { useTranslation } from 'react-i18next';
import { useState, useMemo, useCallback } from 'react';
import { Box, Center, Checkbox, IconButton, Spinner, Text, Flex } from '@chakra-ui/react';
import {
  TableOptions,
  DataTable,
  StatusBadge,
  Pagination,
  type ToggleOption,
  type Column,
  type StatusType,
} from '@themes/aegix';
import {
  useDrillSchedules,
  useDeleteDrillSchedule,
  useUpdateDrillSchedule,
} from '../../hooks/queries';
import { useSites } from '../../hooks/queries';
import {
  patternFromRecurrenceRule,
  type DrillSchedule,
  type DrillScheduleStatus,
  type RecurrencePattern,
} from '../../services';
import { DrillScheduleDrawer } from './drawers/DrillScheduleDrawer';
import { HiChevronDown, HiChevronRight } from 'react-icons/hi2';
import {
  HiCalendarDays,
  HiListBullet,
  HiChevronLeft,
  HiChevronRight as HiChevronRightIcon,
} from 'react-icons/hi2';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import { toaster } from '../ui/toaster';

// ---------- Constants ----------

const statusToggleOptions: ToggleOption[] = [
  { value: 'all', label: 'All', 'data-e2e': 'drill-filter-all' },
  { value: 'scheduled', label: 'Scheduled', 'data-e2e': 'drill-filter-scheduled' },
  { value: 'pending_approval', label: 'Pending Approval', 'data-e2e': 'drill-filter-pending-approval' },
  { value: 'started', label: 'Started', 'data-e2e': 'drill-filter-started' },
  { value: 'completed', label: 'Completed', 'data-e2e': 'drill-filter-completed' },
  { value: 'cancelled', label: 'Cancelled', 'data-e2e': 'drill-filter-cancelled' },
];

const RECURRENCE_LABELS: Record<RecurrencePattern, string> = {
  once: 'One-time',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
  custom: 'Custom',
};

const STATUS_MAP: Record<DrillScheduleStatus, { status: StatusType; label: string }> = {
  scheduled: { status: 'active', label: 'Scheduled' },
  pending_approval: { status: 'pending', label: 'Pending Approval' },
  started: { status: 'processing', label: 'Started' },
  completed: { status: 'completed', label: 'Completed' },
  cancelled: { status: 'inactive', label: 'Cancelled' },
};

// ---------- Row type ----------

interface DrillRow {
  [key: string]: unknown;
  id: string;
  title: string;
  site_name: string;
  alert_type_name: string;
  /** ISO datetime — date + time columns both render from this. */
  scheduled_date: string;
  recurrence: string;
  is_silent: boolean;
  status: StatusType;
  status_raw: DrillScheduleStatus;
}

function transformSchedule(s: DrillSchedule): DrillRow {
  return {
    id: String(s.id),
    title: s.name,
    site_name: s.school_name ?? `Site ${s.school}`,
    alert_type_name: s.alert_type_name ?? (s.alert_type ? `Protocol ${s.alert_type}` : '—'),
    scheduled_date: s.scheduled_date,
    recurrence: RECURRENCE_LABELS[patternFromRecurrenceRule(s.recurrence_rule)],
    is_silent: s.is_silent ?? false,
    status: STATUS_MAP[s.status]?.status ?? ('active' as StatusType),
    status_raw: s.status,
  };
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** Local `YYYY-MM-DD` key for calendar indexing. */
function localDateKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ---------- Calendar helpers ----------

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ---------- Site-grouped list view ----------

interface SiteDrillGroup {
  siteId: number;
  siteName: string;
  rows: DrillRow[];
  schedules: DrillSchedule[];
}

// ---------- Component ----------

export function DrillScheduleList() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [expandedSites, setExpandedSites] = useState<Set<number>>(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<DrillSchedule | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  // Calendar state
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());

  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const handleFilterChange = useCallback((column: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [column]: value }));
  }, []);

  const handleFilterClear = useCallback((column: string) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }, []);

  const filterRows = useCallback(
    (rows: DrillRow[]) => {
      const activeFilters = Object.entries(filterValues).filter(([, v]) => v);
      if (activeFilters.length === 0) return rows;
      return rows.filter((row) =>
        activeFilters.every(([col, val]) => {
          const cellValue = String((row as Record<string, unknown>)[col] ?? '').toLowerCase();
          return cellValue.includes(val.toLowerCase());
        }),
      );
    },
    [filterValues],
  );

  // Data fetching
  const { data: sitesData, isLoading: sitesLoading } = useSites({ page_size: 100 });

  const filters = useMemo(
    () => ({
      search: searchValue || undefined,
      status: statusFilter !== 'all' ? (statusFilter as DrillScheduleStatus) : undefined,
      page,
      page_size: PAGE_SIZE,
      ordering: 'scheduled_date',
    }),
    [searchValue, statusFilter, page],
  );

  const { data: schedulesData, isLoading: schedulesLoading } = useDrillSchedules(filters);
  const deleteMutation = useDeleteDrillSchedule();
  const updateMutation = useUpdateDrillSchedule();

  const isLoading = sitesLoading || schedulesLoading;

  // Columns for DataTable
  const columns = useMemo<Column<DrillRow>[]>(
    () => [
      {
        key: 'actions',
        label: '',
        sortable: false,
        width: '70px',
        render: (row) => (
          <Flex gap={1} justifyContent="center">
            <IconButton
              aria-label={t('sites.editDrill', 'Edit drill')}
              size="xs"
              variant="ghost"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleEditRow(row);
              }}
            >
              <FiEdit size={14} />
            </IconButton>
            <IconButton
              aria-label={t('sites.deleteDrill', 'Delete drill')}
              size="xs"
              variant="ghost"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleDelete(Number(row.id));
              }}
            >
              <FiTrash2 size={14} />
            </IconButton>
          </Flex>
        ),
      },
      { key: 'title', label: t('sites.columns.drill', 'Drill'), sortable: false, filterable: true, width: '200px' },
      {
        key: 'scheduled_date',
        label: t('sites.columns.date', 'Date'),
        sortable: false,
        width: '160px',
        render: (row) => (
          <Text fontSize="sm">{formatDate(row.scheduled_date as string)}</Text>
        ),
      },
      {
        key: 'scheduled_time',
        label: t('sites.columns.time', 'Time'),
        sortable: false,
        width: '90px',
        render: (row) => (
          <Text fontSize="sm">{formatTime(row.scheduled_date as string)}</Text>
        ),
      },
      {
        key: 'alert_type_name',
        label: t('sites.columns.protocol', 'Protocol'),
        sortable: false,
        filterable: true,
        width: '150px',
      },
      {
        key: 'recurrence',
        label: t('sites.columns.recurrence', 'Recurrence'),
        sortable: false,
        filterable: true,
        filterOptions: [
          { label: t('sites.recurrenceOnce', 'One-time'), value: 'One-time' },
          { label: t('sites.recurrenceWeekly', 'Weekly'), value: 'Weekly' },
          { label: t('sites.recurrenceMonthly', 'Monthly'), value: 'Monthly' },
          { label: t('sites.recurrenceQuarterly', 'Quarterly'), value: 'Quarterly' },
          { label: t('sites.recurrenceAnnually', 'Annually'), value: 'Annually' },
        ],
        width: '110px',
      },
      {
        key: 'is_silent',
        label: t('sites.columns.silent', 'Silent'),
        sortable: false,
        width: '70px',
        render: (row) => (
          <Center>
            <Checkbox.Root checked={row.is_silent as boolean} readOnly size="sm">
              <Checkbox.HiddenInput aria-label={t('sites.columns.silent', 'Silent')} />
              <Checkbox.Control />
            </Checkbox.Root>
          </Center>
        ),
      },
      {
        key: 'status',
        label: t('sites.columns.status', 'Status'),
        sortable: false,
        width: '110px',
        render: (row) => (
          <StatusBadge
            status={row.status as StatusType}
            label={STATUS_MAP[row.status_raw as DrillScheduleStatus]?.label ?? row.status_raw}
          />
        ),
      },
    ],
    [],
  );

  // Group schedules by site
  const siteGroups = useMemo<SiteDrillGroup[]>(() => {
    if (!sitesData?.results || !schedulesData?.results) return [];

    const bySite = new Map<number, DrillSchedule[]>();
    for (const s of schedulesData.results) {
      const list = bySite.get(s.school) || [];
      list.push(s);
      bySite.set(s.school, list);
    }

    const groups: SiteDrillGroup[] = [];
    for (const site of sitesData.results) {
      const schedules = bySite.get(site.id) || [];
      if (schedules.length > 0 || !searchValue) {
        groups.push({
          siteId: site.id,
          siteName: site.name,
          rows: schedules.map(transformSchedule),
          schedules,
        });
      }
    }

    // Include any schedules for sites not in the page
    const knownSiteIds = new Set(sitesData.results.map((s) => s.id));
    const orphanedBySite = new Map<number, DrillSchedule[]>();
    for (const s of schedulesData.results) {
      if (!knownSiteIds.has(s.school)) {
        const list = orphanedBySite.get(s.school) || [];
        list.push(s);
        orphanedBySite.set(s.school, list);
      }
    }
    for (const [siteId, schedules] of orphanedBySite) {
      groups.push({
        siteId,
        siteName: schedules[0]?.school_name ?? `Site ${siteId}`,
        rows: schedules.map(transformSchedule),
        schedules,
      });
    }

    return groups;
  }, [sitesData, schedulesData, searchValue]);

  // Calendar: schedules indexed by local YYYY-MM-DD (scheduled_date is an
  // ISO datetime on the canonical contract).
  const calendarEntries = useMemo(() => {
    if (!schedulesData?.results) return new Map<string, DrillSchedule[]>();
    const map = new Map<string, DrillSchedule[]>();
    for (const s of schedulesData.results) {
      const key = localDateKey(s.scheduled_date);
      const list = map.get(key) || [];
      list.push(s);
      map.set(key, list);
    }
    return map;
  }, [schedulesData]);

  // ---------- Handlers ----------

  const toggleSite = (siteId: number) => {
    setExpandedSites((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  };

  const findScheduleById = (id: string): DrillSchedule | undefined => {
    for (const group of siteGroups) {
      const found = group.schedules.find((s) => String(s.id) === id);
      if (found) return found;
    }
    return undefined;
  };

  const handleEditRow = (row: DrillRow) => {
    const schedule = findScheduleById(row.id);
    if (schedule) {
      setEditingSchedule(schedule);
      setDrawerOpen(true);
    }
  };

  const handleRowClick = (row: DrillRow) => {
    const schedule = findScheduleById(row.id);
    if (schedule) {
      setEditingSchedule(schedule);
      setDrawerOpen(true);
    }
  };

  const handleAddDrill = () => {
    setEditingSchedule(null);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingSchedule(null);
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toaster.create({ title: t('sites.drillScheduleDeleted', 'Drill schedule deleted'), type: 'success', duration: 3000 });
      },
      onError: (err) => {
        toaster.create({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to delete',
          type: 'error',
          duration: 4000,
        });
      },
    });
  };

  const handleCalendarScheduleClick = (schedule: DrillSchedule) => {
    setEditingSchedule(schedule);
    setDrawerOpen(true);
  };

  const prevMonth = () => {
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear(calYear - 1);
    } else {
      setCalMonth(calMonth - 1);
    }
  };

  const nextMonth = () => {
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear(calYear + 1);
    } else {
      setCalMonth(calMonth + 1);
    }
  };

  const totalPages = schedulesData ? Math.ceil(schedulesData.count / PAGE_SIZE) : 1;

  // ---------- Render ----------

  const statusToggleOptions: ToggleOption[] = useMemo(() => [
    { value: 'all', label: t('common.all', 'All'), 'data-e2e': 'drill-filter-all' },
    { value: 'scheduled', label: t('sites.statusScheduled', 'Scheduled'), 'data-e2e': 'drill-filter-scheduled' },
    { value: 'completed', label: t('sites.statusCompleted', 'Completed'), 'data-e2e': 'drill-filter-completed' },
    { value: 'cancelled', label: t('sites.statusCancelled', 'Cancelled'), 'data-e2e': 'drill-filter-cancelled' },
  ], [t]);

  return (
    <>
      <TableOptions
        toggleOptions={statusToggleOptions}
        activeToggle={statusFilter}
        onToggleChange={(v: string) => {
          setStatusFilter(v);
          setPage(1);
        }}
        searchValue={searchValue}
        onSearchChange={(v: string) => {
          setSearchValue(v);
          setPage(1);
        }}
        showFilter={false}
        primaryAction={{
          label: t('sites.scheduleDrillTitle', 'Schedule Drill'),
          onClick: handleAddDrill,
        }}
      />

      <Box px={5} pb={5}>
        {/* View toggle + pagination info */}
        <Flex mb={3} justifyContent="space-between" alignItems="center">
          <Flex gap={1}>
            <IconButton
              aria-label={t('sites.listView', 'List view')}
              size="sm"
              variant={viewMode === 'list' ? 'outline' : 'ghost'}
              onClick={() => setViewMode('list')}
            >
              <HiListBullet size={18} />
            </IconButton>
            <IconButton
              aria-label={t('sites.calendarView', 'Calendar view')}
              size="sm"
              variant={viewMode === 'calendar' ? 'outline' : 'ghost'}
              onClick={() => setViewMode('calendar')}
            >
              <HiCalendarDays size={18} />
            </IconButton>
          </Flex>

          {viewMode === 'list' && schedulesData && (
            <Text fontSize="xs" color="text.secondary">
              {schedulesData.count} {schedulesData.count === 1 ? t('sites.drillSingle', 'drill') : t('sites.drillsPlural', 'drills')}
            </Text>
          )}
        </Flex>

        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : viewMode === 'list' ? (
          /* ========= LIST VIEW ========= */
          <>
            {siteGroups.length === 0 && !searchValue ? (
              <Center py={12}>
                <Text color="text.secondary">
                  {t('sites.noDrillSchedulesYet', 'No drill schedules yet. Click "Schedule Drill" to create one.')}
                </Text>
              </Center>
            ) : siteGroups.length === 0 ? (
              <Center py={12}>
                <Text color="text.secondary">{t('sites.noDrillsFound', 'No drills found')}</Text>
              </Center>
            ) : (
              <Box>
                {siteGroups.map((group) => {
                  const isExpanded = expandedSites.has(group.siteId);

                  return (
                    <Box
                      key={group.siteId}
                      mb={3}
                      border="1px solid"
                      borderColor="border.secondary"
                      borderRadius="lg"
                      overflow="hidden"
                    >
                      <Flex
                        px={4}
                        py={3}
                        alignItems="center"
                        justifyContent="space-between"
                        cursor="pointer"
                        bg="surface.secondary"
                        _hover={{ bg: 'surface.tertiary' }}
                        onClick={() => toggleSite(group.siteId)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleSite(group.siteId);
                          }
                        }}
                        data-e2e="drill-site-group-button"
                      >
                        <Flex alignItems="center" gap={2}>
                          {isExpanded ? (
                            <HiChevronDown size={18} />
                          ) : (
                            <HiChevronRight size={18} />
                          )}
                          <Text fontWeight={600} fontSize="sm" color="text.primary">
                            {group.siteName}
                          </Text>
                        </Flex>
                        <Text fontSize="xs" color="text.secondary">
                          {group.rows.length} {group.rows.length === 1 ? t('sites.drillSingle', 'drill') : t('sites.drillsPlural', 'drills')}
                        </Text>
                      </Flex>

                      {isExpanded && (
                        <Box>
                          {group.rows.length === 0 ? (
                            <Center py={6}>
                              <Text fontSize="sm" color="text.secondary">
                                {t('sites.noDrillsScheduledForSite', 'No drills scheduled for this site')}
                              </Text>
                            </Center>
                          ) : (
                            <DataTable
                              columns={columns}
                              data={filterRows(group.rows)}
                              keyExtractor={(row) => row.id}
                              filterValues={filterValues}
                              onFilterChange={handleFilterChange}
                              onFilterClear={handleFilterClear}
                              onRowClick={handleRowClick}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {totalPages > 1 && (
              <Box mt={4}>
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  pageSize={PAGE_SIZE}
                />
              </Box>
            )}
          </>
        ) : (
          /* ========= CALENDAR VIEW ========= */
          <Box>
            {/* Month navigation */}
            <Flex alignItems="center" justifyContent="space-between" mb={4}>
              <IconButton
                aria-label={t('sites.prevMonth', 'Previous month')}
                size="sm"
                variant="ghost"
                onClick={prevMonth}
              >
                <HiChevronLeft size={20} />
              </IconButton>
              <Text fontWeight={600} fontSize="md" color="text.primary">
                {MONTH_NAMES[calMonth]} {calYear}
              </Text>
              <IconButton
                aria-label={t('sites.nextMonth', 'Next month')}
                size="sm"
                variant="ghost"
                onClick={nextMonth}
              >
                <HiChevronRightIcon size={20} />
              </IconButton>
            </Flex>

            {/* Day headers */}
            <Flex>
              {DAY_LABELS.map((d) => (
                <Box
                  key={d}
                  flex={1}
                  textAlign="center"
                  py={1}
                  fontSize="xs"
                  fontWeight={600}
                  color="text.secondary"
                >
                  {d}
                </Box>
              ))}
            </Flex>

            {/* Calendar grid */}
            <CalendarGrid
              year={calYear}
              month={calMonth}
              entries={calendarEntries}
              onScheduleClick={handleCalendarScheduleClick}
            />
          </Box>
        )}
      </Box>

      <DrillScheduleDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        schedule={editingSchedule}
      />
    </>
  );
}

// ---------- Calendar grid sub-component ----------

function CalendarGrid({
  year,
  month,
  entries,
  onScheduleClick,
}: {
  year: number;
  month: number;
  entries: Map<string, DrillSchedule[]>;
  onScheduleClick: (s: DrillSchedule) => void;
}) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const today = new Date();
  const todayStr =
    today.getFullYear() === year && today.getMonth() === month
      ? String(today.getDate())
      : null;

  const cells: React.ReactNode[] = [];

  // Empty cells before first day
  for (let i = 0; i < firstDay; i++) {
    cells.push(
      <Box key={`empty-${i}`} flex={1} minH="80px" data-e2e="calendar-day-cell" />,
    );
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const daySchedules = entries.get(dateStr) || [];
    const isToday = todayStr === String(day);

    cells.push(
      <Box
        key={day}
        flex={1}
        minH="80px"
        border="1px solid"
        borderColor="border.primary"
        p={1}
        bg={isToday ? 'surface.secondary' : 'transparent'}
        data-e2e="calendar-day-cell"
      >
        <Text
          fontSize="xs"
          fontWeight={isToday ? 700 : 500}
          color={isToday ? 'brand.500' : 'text.secondary'}
          mb={0.5}
        >
          {day}
        </Text>
        {daySchedules.slice(0, 3).map((s) => (
          <Box
            key={s.id}
            px={1}
            py={0.5}
            mb={0.5}
            borderRadius="sm"
            fontSize="2xs"
            lineHeight="1.2"
            cursor="pointer"
            bg={
              s.status === 'completed'
                ? 'green.100'
                : s.status === 'cancelled'
                  ? 'red.100'
                  : 'surface.drill/20'
            }
            color={
              s.status === 'completed'
                ? 'button.success'
                : s.status === 'cancelled'
                  ? 'surface.danger'
                  : 'surface.drill'
            }
            _hover={{ opacity: 0.8 }}
            onClick={() => onScheduleClick(s)}
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onScheduleClick(s);
              }
            }}
            title={`${s.name} — ${formatTime(s.scheduled_date)}`}
          >
            <Text lineClamp={1}>{s.name}</Text>
          </Box>
        ))}
        {daySchedules.length > 3 && (
          <Text fontSize="2xs" color="text.secondary">
            +{daySchedules.length - 3} more
          </Text>
        )}
      </Box>,
    );
  }

  // Build rows of 7
  const rows: React.ReactNode[] = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(
      <Flex key={`row-${i}`}>
        {cells.slice(i, i + 7)}
        {/* Pad last row */}
        {i + 7 > cells.length &&
          Array.from({ length: 7 - (cells.length - i) }).map((_, j) => (
            <Box key={`pad-${j}`} flex={1} minH="80px" data-e2e="calendar-day-cell" />
          ))}
      </Flex>,
    );
  }

  return <Box>{rows}</Box>;
}
